import * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

/**
 * Handoff — the post-sign-in moment (canvas 01b `auth/Handoff`,
 * `auth/HandoffFailed`, MV2-910).
 *
 * Replaces the naked 24 px spinner that spun forever when /me failed. Full
 * viewport on `--gray-0`: the platform mark, ONE line naming where you are
 * going, one meta line naming who you are, and a 220 px bar. Failure gets
 * Retry and Sign out, never an infinite spin. Pure presentation — the
 * `DashboardRouter` owns the fetch, the retries and the navigation.
 */
export type HandoffProps =
  | {
      state: "loading";
      /** "Signed in as padraig.ryan@…" once the session has an email. */
      identity?: string | null;
    }
  | {
      state: "ready";
      /** "Inbox", "Overview", "Platform health", "My plants". */
      destination: string;
      /** "Cillian O'Donnell · O&M · Verde Energy Group". */
      identity: string;
    }
  | {
      state: "failed";
      retries: number;
      requestId?: string | null;
      onRetry: () => void;
      onSignOut: () => void;
      busy?: boolean;
    };

export function Handoff(props: HandoffProps): React.ReactElement {
  const failed = props.state === "failed";
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={props.state !== "failed" || undefined}
      className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-4 bg-[color:var(--gray-0)] px-6 text-center"
    >
      <span
        aria-hidden
        className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-[color:var(--brand-primary)] text-[15px] font-bold text-[color:var(--brand-primary-fg)]"
      >
        re
      </span>

      <div className="flex flex-col items-center gap-1">
        <h2 className="text-[16px] font-semibold tracking-[-0.01em] text-gray-900">
          {props.state === "ready"
            ? `Taking you to your ${props.destination}`
            : failed
              ? "We couldn't load your profile"
              : "Signing you in"}
        </h2>
        <p className="text-[11px] leading-[1.4] text-gray-500">
          {props.state === "ready" ? (
            `Signed in as ${props.identity}`
          ) : failed ? (
            <>
              Retried <span className="tabular">{props.retries}×</span>
              {props.requestId ? (
                <>
                  {" · "}
                  <span className="font-mono tabular">{props.requestId}</span>
                </>
              ) : null}
              {" · your session is fine"}
            </>
          ) : props.identity ? (
            `Signed in as ${props.identity}`
          ) : (
            "Loading your profile"
          )}
        </p>
      </div>

      {failed ? (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={props.onRetry}
            disabled={props.busy}
          >
            {props.busy ? "Retrying…" : "Retry"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={props.onSignOut}
            disabled={props.busy}
          >
            Sign out
          </Button>
        </div>
      ) : (
        <div
          role="progressbar"
          aria-label="Signing in"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={props.state === "ready" ? 60 : 30}
          className="relative h-[5px] w-[220px] overflow-hidden rounded-[3px] bg-gray-100"
        >
          {/* Persistent colour, no animation — the width steps as the state does (CROSS_PAGE §5). */}
          <i
            className={cn(
              "absolute inset-y-0 left-0 rounded-[3px] bg-[color:var(--action)]",
              props.state === "ready" ? "w-[60%]" : "w-[30%]",
            )}
          />
        </div>
      )}
    </div>
  );
}
